import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';

import { supabase } from '../../lib/supabase';
import { Booking, getBooking } from '../../lib/bookings';
import BookingCustomerInfo from './BookingCustomerInfo';
import BookingServiceList from './BookingServiceList';
import BookingProductList from './BookingProductList';
import ScheduleTimeline from './ScheduleTimeline';
import BookingResourceList from './BookingResourceList';
import BookingPaymentSummary from './BookingPaymentSummary';
import BookingMaterialsTab from './BookingMaterialsTab';
import BookingFlatRatesTab from './BookingFlatRatesTab';
import { usePermissionCheck } from '../../hooks/usePermissionCheck';
import { getBookingCommissions } from '../../lib/bookingCommissions';
import { useBusiness } from '../../contexts/BusinessContext';

import BookingProfitLoss from './BookingProfitLoss';
import BookingCommissionSection from './BookingCommissionSection';
import { format } from 'date-fns';
import { updateBooking } from '../../lib/bookings';
import ApplyDiscountModal from '../ApplyDiscountModal';
import RecordPaymentModal from '../RecordPaymentModal';
import PaymentDetailsModal from '../PaymentDetailsModal';
import BetaPaymentsModal from '../BetaPaymentsModal';
import { Payment } from '../../lib/payments';
import { ExternalLink, Copy, CheckCircle, Star, ChevronRight, ChevronDown, ChevronLeft, XCircle, Clock, Calendar, Circle, Shield, Info, Save, Edit3 } from 'lucide-react';
import { generateFeedbackForBooking, generateFeedbackURL, getBookingFeedback, isFeedbackEnabled, updateFeedbackRatings, type BookingFeedback } from '../../lib/feedback';
import { getAllFeedbackFormFields, type FeedbackFormField } from '../../lib/feedbackForm';
import { 
  getLiabilityWaiverByBookingId, 
  sendLiabilityWaiverSMS, 
  sendLiabilityWaiverEmail,
  isLiabilityWaiverEnabled,
  generateLiabilityWaiver,
  generateLiabilityWaiverURL,
  type LiabilityWaiver 
} from '../../lib/liabilityWaivers';
import SendLiabilityWaiverModal from '../SendLiabilityWaiverModal';
import SendInvoiceModal from '../SendInvoiceModal';
import VehicleDamageReportModal from '../VehicleDamageReportModal';
import { sendGmailEmail } from '../../lib/email';
import EditableStarRating from './EditableStarRating';

// Custom portal-based toast component for modal contexts
const PortalToast = ({ 
  children, 
  type, 
  onClose 
}: { 
  children: React.ReactNode; 
  type: 'loading' | 'success' | 'error';
  onClose: () => void;
}) => {
  const getIcon = () => {
    switch (type) {
      case 'loading':
        return <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>;
      case 'success':
        return <div className="rounded-full h-4 w-4 bg-green-500 flex items-center justify-center text-white text-xs">✓</div>;
      case 'error':
        return <div className="rounded-full h-4 w-4 bg-red-500 flex items-center justify-center text-white text-xs">✕</div>;
    }
  };

  const getBorderColor = () => {
    switch (type) {
      case 'loading':
        return 'border-blue-200';
      case 'success':
        return 'border-green-200';
      case 'error':
        return 'border-red-200';
    }
  };

  return createPortal(
    <div 
      className={`fixed top-4 left-1/2 transform -translate-x-1/2 bg-white rounded-lg shadow-lg border ${getBorderColor()} p-4 flex items-center space-x-3 min-w-[300px] max-w-md`}
      style={{ zIndex: 999999 }}
    >
      {getIcon()}
      <div className="flex-1">
        {children}
      </div>
      {type !== 'loading' && (
        <button 
          onClick={onClose}
          className="text-gray-400 hover:text-gray-600 ml-2"
        >
          ×
        </button>
      )}
    </div>,
    document.body
  );
};

// Custom toast function with portal rendering for modal contexts
const createPortalToast = (
  setShowPortalToast: (show: boolean) => void,
  setPortalToastMessage: (message: React.ReactNode) => void,
  setPortalToastType: (type: 'loading' | 'success' | 'error') => void
) => ({
  loading: (message: React.ReactNode, options?: any) => {
    setPortalToastMessage(message);
    setPortalToastType('loading');
    setShowPortalToast(true);
    return 'portal-toast-loading';
  },
  success: (message: string, options?: any) => {
    setPortalToastMessage(message);
    setPortalToastType('success');
    setShowPortalToast(true);
    // Auto-hide success toast after 4 seconds
    setTimeout(() => setShowPortalToast(false), 4000);
    return 'portal-toast-success';
  },
  error: (message: string, options?: any) => {
    setPortalToastMessage(message);
    setPortalToastType('error');
    setShowPortalToast(true);
    // Auto-hide error toast after 4 seconds
    setTimeout(() => setShowPortalToast(false), 4000);
    return 'portal-toast-error';
  },
  dismiss: (toastId?: string) => {
    setShowPortalToast(false);
  }
});

import { 
  getDamageReportByBookingId, 
  hasDamageReport,
  type VehicleDamageReport 
} from '../../lib/damageReports';

interface DynamicFeedbackResponse {
  [fieldKey: string]: string | number;
}

interface ExtendedBookingFeedback extends BookingFeedback {
  form_responses?: DynamicFeedbackResponse;
}

interface BookingContentProps {
  activeTab: string;
  booking: Booking;
  isSelectMaterialModalOpen: boolean;
  setIsSelectMaterialModalOpen: (isOpen: boolean) => void;
  onEditCustomer: () => void;
  onEditVehicle: (vehicleId: string) => void;
  onAddService: (vehicleId: string) => void;
  onRemoveResource: (resourceId: string) => Promise<void>;
  onAddResource: () => void;
  onApplyDiscount: () => void;
  onDeleteDiscount: (discountId: string) => Promise<void>;
  onUpdate: () => Promise<void>;
  onError: (error: string) => void;
  onAddressUpdate?: (address: string) => Promise<void>;
  onRescheduleMobileBooking?: () => void;
  onStatusChange?: (status: string) => Promise<void>;
  onScheduleChange?: (dropOffDate: string, dropOffTime: string, startDate: string, startTime: string, endDate: string, endTime: string) => Promise<void>;
  // Damage and waiver state callbacks
  onDamageWaiverStateChange?: (state: {
    isLiabilityWaiverEnabled: boolean;
    liabilityWaiverAgreed: boolean;
    hasDamageReport: boolean;
    liabilityWaiverPdfUrl?: string;
    onSendLiabilityWaiver: () => void;
    onAddDamageReport: () => void;
    isSendingLiabilityWaiver: boolean;
  }) => void;
  onAssignWorkflow?: (serviceId: string) => void; // Callback to handle workflow assignment
  onViewInvoiceFromHeader?: () => void; // Callback to handle view invoice from header
}

export default function BookingContent({
  activeTab,
  booking,
  isSelectMaterialModalOpen,
  setIsSelectMaterialModalOpen,
  onEditCustomer,
  onEditVehicle,
  onAddService,
  onRemoveResource,
  onAddResource,
  onApplyDiscount,
  onDeleteDiscount,
  onUpdate,
  onError,
  onAddressUpdate,
  onRescheduleMobileBooking,
  onStatusChange,
  onScheduleChange,
  onDamageWaiverStateChange,
  onAssignWorkflow,
  onViewInvoiceFromHeader
}: BookingContentProps) {
  const { canAccess } = usePermissionCheck();
  const { selectedBusiness } = useBusiness();
  const [isApplyDiscountModalOpen, setIsApplyDiscountModalOpen] = useState(false);
  const [isRecordPaymentModalOpen, setIsRecordPaymentModalOpen] = useState(false);
  const [isPaymentDetailsModalOpen, setIsPaymentDetailsModalOpen] = useState(false);
  const [isBetaPaymentsModalOpen, setIsBetaPaymentsModalOpen] = useState(false);
  const [isSendInvoiceModalOpen, setIsSendInvoiceModalOpen] = useState(false);
  const [isInvisiblePDFGeneration, setIsInvisiblePDFGeneration] = useState(false);
  const [showPortalToast, setShowPortalToast] = useState(false);
  const [portalToastMessage, setPortalToastMessage] = useState<React.ReactNode>(null);
  const [portalToastType, setPortalToastType] = useState<'loading' | 'success' | 'error'>('loading');
  const [selectedPayment, setSelectedPayment] = useState<Payment | null>(null);

  // Create portal toast instance
  const portalToast = createPortalToast(setShowPortalToast, setPortalToastMessage, setPortalToastType);
  const [isLoadingInvoice, setIsLoadingInvoice] = useState(false);
  const [isEditingTime, setIsEditingTime] = useState(false);
  const [timeFormData, setTimeFormData] = useState({
    drop_off_date: '',
    drop_off_time: '',
    start_date: '',
    start_time: '',
    end_date: '',
    end_time: ''
  });
  const [isColorPickerOpen, setIsColorPickerOpen] = useState(false);

  const [customerNotes, setCustomerNotes] = useState(booking.customer_notes || '');
  const [productTotals, setProductTotals] = useState({
    subtotal: 0,
    taxAmount: 0,
    total: 0
  });

  // Function to handle product totals update from BookingProductList
  const handleProductTotalsUpdate = (totals: { subtotal: number; taxAmount: number; total: number }) => {
    setProductTotals(totals);
  };
  const [feedbackLink, setFeedbackLink] = useState<string | null>(null);
  const [isGeneratingFeedback, setIsGeneratingFeedback] = useState(false);
  const [feedbackCopied, setFeedbackCopied] = useState(false);
  const [bookingFeedback, setBookingFeedback] = useState<BookingFeedback | null>(null);
  const [showFeedbackLink, setShowFeedbackLink] = useState(false);
  const [showIndividualRatings, setShowIndividualRatings] = useState(false);
  const [isFeedbackEnabledForBusiness, setIsFeedbackEnabledForBusiness] = useState(true);
  
  // Feedback editing state
  const [isEditingFeedback, setIsEditingFeedback] = useState(false);
  const [editingFeedbackData, setEditingFeedbackData] = useState<{
    overall_quality_rating?: number;
    overall_experience_rating?: number;
    form_responses?: { [fieldKey: string]: string | number };
  }>({});
  const [isSavingFeedback, setIsSavingFeedback] = useState(false);
  
  
  const [feedbackFormFields, setFeedbackFormFields] = useState<FeedbackFormField[]>([]);
  const [allPaymentDetails, setAllPaymentDetails] = useState<any[]>([]);
  const [isLiabilityWaiverEnabledForBusiness, setIsLiabilityWaiverEnabledForBusiness] = useState(false);
  const [liabilityWaiver, setLiabilityWaiver] = useState<LiabilityWaiver | null>(null);
  const [isLoadingLiabilityWaiver, setIsLoadingLiabilityWaiver] = useState(true);
  const [isSendingLiabilityWaiver, setIsSendingLiabilityWaiver] = useState(false);
  const [isSendLiabilityWaiverModalOpen, setIsSendLiabilityWaiverModalOpen] = useState(false);
  const [damageReport, setDamageReport] = useState<VehicleDamageReport | null>(null);
  const [isDamageReportModalOpen, setIsDamageReportModalOpen] = useState(false);
  const [originalBooking, setOriginalBooking] = useState<Booking | null>(null);
  const [showServiceDetails, setShowServiceDetails] = useState(false);
  const [originalBookingServices, setOriginalBookingServices] = useState<any[]>([]);

  const dropOffDateErrorId = 'drop-off-date-error';
  const dropOffTimeErrorId = 'drop-off-time-error';
  const startDateErrorId = 'start-date-error';
  const startTimeErrorId = 'start-time-error';
  const endDateErrorId = 'end-date-error';
  const endTimeErrorId = 'end-time-error';

  // Initialize form data when booking changes
  React.useEffect(() => {
    if (booking) {
      setTimeFormData({
        drop_off_date: booking.drop_off_date || '',
        drop_off_time: booking.drop_off_time || '',
        start_date: booking.start_date || '',
        start_time: booking.start_time || '',
        end_date: booking.end_date || '',
        end_time: booking.end_time || ''
      });
      setCustomerNotes(booking.customer_notes || '');
    }
  }, [booking]);

  // Check if feedback is enabled for this business
  React.useEffect(() => {
    const checkFeedbackEnabled = async () => {
      if (booking.business_id) {
        try {
          const enabled = await isFeedbackEnabled(booking.business_id);
          setIsFeedbackEnabledForBusiness(enabled);
        } catch (error) {
          console.error('Error checking feedback enabled status:', error);
          setIsFeedbackEnabledForBusiness(false);
        }
      }
    };
    
    checkFeedbackEnabled();
  }, [booking.business_id]);

  // Use preloaded liability waiver data from booking
  React.useEffect(() => {
    if (booking.liability_waiver_enabled !== undefined) {
      // Use preloaded data - instant loading!
      setIsLiabilityWaiverEnabledForBusiness(booking.liability_waiver_enabled);
      setLiabilityWaiver(booking.liability_waiver || null);
      setIsLoadingLiabilityWaiver(false);
    } else {
      // Fallback to loading if preloaded data is not available
      const checkLiabilityWaiverEnabled = async () => {
        if (booking.business_id && supabase) {
          try {
            setIsLoadingLiabilityWaiver(true);
            const enabled = await isLiabilityWaiverEnabled(booking.business_id);
            setIsLiabilityWaiverEnabledForBusiness(enabled);
            
            // If enabled, load existing waiver data for this booking
            if (enabled) {
              const existingWaiver = await getLiabilityWaiverByBookingId(booking.id);
              setLiabilityWaiver(existingWaiver);
            } else {
              setLiabilityWaiver(null);
            }
          } catch (error) {
            console.error('Error checking liability waiver enabled status:', error);
            setIsLiabilityWaiverEnabledForBusiness(false);
            setLiabilityWaiver(null);
          } finally {
            setIsLoadingLiabilityWaiver(false);
          }
        } else {
          setIsLoadingLiabilityWaiver(false);
        }
      };
      
      checkLiabilityWaiverEnabled();
    }
  }, [booking.business_id, booking.id, booking.liability_waiver_enabled, booking.liability_waiver]);

  // Load original booking details for warranty bookings
  React.useEffect(() => {
    const loadOriginalBooking = async () => {
              if (booking.is_warranty && booking.original_booking_id) {
          try {
            console.log('🛡️ Loading original booking details for warranty booking:', booking.original_booking_id);
            const original = await getBooking(booking.original_booking_id);
            setOriginalBooking(original);
            
            // Fetch the booking services for the original booking
            if (original?.id) {
              const { data: services, error } = await supabase
                .from('booking_services')
                .select(`
                  id,
                  service_id,
                  name,
                  description,
                  price,
                  tax_amount,
                  total_amount,
                  duration_minutes,
                  services (
                    id,
                    name,
                    description
                  )
                `)
                .eq('booking_id', original.id);
              
              if (error) {
                console.error('Error loading original booking services:', error);
              } else {
                console.log('🛡️ Loaded original booking services:', services);
                setOriginalBookingServices(services || []);
              }
            }
          } catch (error) {
            console.error('Error loading original booking:', error);
            setOriginalBooking(null);
            setOriginalBookingServices([]);
          }
        } else {
          setOriginalBooking(null);
          setOriginalBookingServices([]);
        }
    };
    
    loadOriginalBooking();
  }, [booking.is_warranty, booking.original_booking_id]);

  // Use preloaded damage report data from booking
  React.useEffect(() => {
    if (booking.damage_report !== undefined) {
      // Use preloaded data - instant loading!
      setDamageReport(booking.damage_report);
    } else {
      // Fallback to loading if preloaded data is not available
      const loadDamageReport = async () => {
        if (booking.id && isLiabilityWaiverEnabledForBusiness) {
          try {
            const existingDamageReport = await getDamageReportByBookingId(booking.id);
            setDamageReport(existingDamageReport);
          } catch (error) {
            console.error('Error loading damage report:', error);
            setDamageReport(null);
          }
        }
      };
      
      loadDamageReport();
    }
  }, [booking.id, booking.damage_report, isLiabilityWaiverEnabledForBusiness]);

  // Generate feedback link for completed bookings
  React.useEffect(() => {
    const generateFeedbackLink = async () => {
      if (booking.status === 'completed' && isFeedbackEnabledForBusiness) {
        if (booking.feedback_token) {
          // If we already have a token, generate the URL
          const url = generateFeedbackURL(booking.feedback_token);
          setFeedbackLink(url);
        } else {
          // If completed but no token, try to generate one
          try {
            setIsGeneratingFeedback(true);
            const token = await generateFeedbackForBooking(booking.id);
            if (token) {
              const url = generateFeedbackURL(token);
              setFeedbackLink(url);
              // Try to refresh booking, but don't fail if it doesn't work
              try {
                await onUpdate();
              } catch (updateError) {
                console.warn('Could not refresh booking after feedback generation:', updateError);
                // Still show the link even if booking refresh fails
              }
            } else {
              console.error('Failed to generate feedback token');
            }
          } catch (error) {
            console.error('Error generating feedback link:', error);
          } finally {
            setIsGeneratingFeedback(false);
          }
        }
      } else {
        setFeedbackLink(null);
      }
    };

    generateFeedbackLink();
  }, [booking.status, booking.feedback_token, booking.id, isFeedbackEnabledForBusiness]);

  // Load feedback results for completed bookings
  React.useEffect(() => {
    const loadBookingFeedback = async () => {
      if (booking.status === 'completed') {
        try {
          const feedback = await getBookingFeedback(booking.id);
          setBookingFeedback(feedback);
        } catch (error) {
          console.error('Error loading booking feedback:', error);
        }
      } else {
        setBookingFeedback(null);
      }
    };

    loadBookingFeedback();
  }, [booking.status, booking.id]);

  // Load feedback form fields for this business
  React.useEffect(() => {
    const loadFeedbackFormFields = async () => {
      if (booking.business_id) {
        try {
          const fields = await getAllFeedbackFormFields(booking.business_id);
          setFeedbackFormFields(fields);
        } catch (error) {
          console.error('Error loading feedback form fields:', error);
          setFeedbackFormFields([]);
        }
      }
    };

    loadFeedbackFormFields();
  }, [booking.business_id]);

  const handleCopyFeedbackLink = async () => {
    if (feedbackLink) {
      try {
        await navigator.clipboard.writeText(feedbackLink);
        setFeedbackCopied(true);
        setTimeout(() => setFeedbackCopied(false), 2000);
      } catch (error) {
        console.error('Failed to copy feedback link:', error);
        onError('Failed to copy feedback link');
      }
    }
  };

  const handleGenerateFeedbackLink = async () => {
    try {
      setIsGeneratingFeedback(true);
      const token = await generateFeedbackForBooking(booking.id);
      if (token) {
        const url = generateFeedbackURL(token);
        setFeedbackLink(url);
        // Try to refresh booking, but don't fail if it doesn't work
        try {
          await onUpdate();
        } catch (updateError) {
          console.warn('Could not refresh booking after feedback generation:', updateError);
          // Still show the link even if booking refresh fails
        }
      } else {
        onError('Failed to generate feedback link');
      }
    } catch (error) {
      console.error('Error generating feedback link:', error);
      onError('Failed to generate feedback link');
    } finally {
      setIsGeneratingFeedback(false);
    }
  };

  // Feedback editing functions
  const handleStartEditingFeedback = () => {
    if (!bookingFeedback) return;
    
    // Initialize editing data with current feedback values
    const responses = (bookingFeedback as ExtendedBookingFeedback).form_responses || {};
    setEditingFeedbackData({
      overall_quality_rating: bookingFeedback.overall_quality_rating,
      overall_experience_rating: bookingFeedback.overall_experience_rating,
      form_responses: Object.keys(responses).length > 0 ? { ...responses } : undefined
    });
    setIsEditingFeedback(true);
  };

  const handleCancelEditingFeedback = () => {
    setIsEditingFeedback(false);
    setEditingFeedbackData({});
  };

  const handleSaveFeedback = async () => {
    if (!bookingFeedback) return;
    
    try {
      setIsSavingFeedback(true);
      
      const updatedFeedback = await updateFeedbackRatings(bookingFeedback.id, editingFeedbackData);
      
      if (updatedFeedback) {
        setBookingFeedback(updatedFeedback);
        setIsEditingFeedback(false);
        setEditingFeedbackData({});
        onError('Feedback ratings updated successfully');
      }
    } catch (error) {
      console.error('Error saving feedback ratings:', error);
      onError(error instanceof Error ? error.message : 'Failed to save feedback ratings');
    } finally {
      setIsSavingFeedback(false);
    }
  };

  const handleRatingChange = (fieldKey: string, newRating: number) => {
    setEditingFeedbackData(prev => {
      const responses = (bookingFeedback as ExtendedBookingFeedback).form_responses || {};
      
      if (Object.keys(responses).length > 0) {
        // Dynamic form responses
        return {
          ...prev,
          form_responses: {
            ...prev.form_responses,
            [fieldKey]: newRating
          }
        };
      } else {
        // Legacy fields
        if (fieldKey === 'overall_quality_rating') {
          return { ...prev, overall_quality_rating: newRating };
        } else if (fieldKey === 'overall_experience_rating') {
          return { ...prev, overall_experience_rating: newRating };
        }
      }
      
      return prev;
    });
  };

  // Helper component to render star ratings
  const StarRating = ({ rating, size = 'small' }: { rating: number; size?: 'small' | 'large' }) => {
    const starSize = size === 'large' ? 'h-5 w-5' : 'h-4 w-4';
    
    return (
      <div className="flex space-x-1">
        {[1, 2, 3, 4, 5].map((star) => (
          <Star
            key={star}
            className={`${starSize} ${
              star <= rating ? 'text-yellow-400 fill-yellow-400' : 'text-gray-300'
            }`}
          />
        ))}
      </div>
    );
  };

  const handleViewInvoiceBeta = () => {
    // Open SendInvoiceModal for sending invoice functionality
    setIsSendInvoiceModalOpen(true);
  };

  // Handle send method selection from SendInvoiceModal
  const handleSendMethodSelect = async (method: 'email' | 'sms', allowStripePay: boolean) => {
    console.log('BookingContent - Method selected:', method, 'Allow Stripe Pay:', allowStripePay);
    
    // Close the SendInvoiceModal first
    setIsSendInvoiceModalOpen(false);
    
    if (method === 'email') {
      // Send invoice directly without opening BetaPaymentsModal
      await handleDirectSendInvoice(allowStripePay);
    } else {
      // SMS functionality coming soon
      portalToast.error('SMS functionality coming soon!');
    }
  };

  // Send invoice directly without opening any modals
  const handleDirectSendInvoice = async (allowStripePay: boolean = true) => {
    if (!booking.customer?.email || !selectedBusiness) {
      portalToast.error('Customer email not found');
      return;
    }

    // Show immediate sending feedback with loading
    const loadingToast = portalToast.loading(
      <div>
        <div>Sending invoice...</div>
        <div style={{ fontSize: '12px', opacity: 0.7, marginTop: '2px' }}>don't close booking</div>
      </div>, 
      {
        position: 'top-center'
      }
    );

    try {
      // Step 1: Open BetaPaymentsModal invisibly to generate PDF
      setIsInvisiblePDFGeneration(true);
      setIsBetaPaymentsModalOpen(true);

      // Wait longer for modal to fully render and all data to load
      await new Promise(resolve => setTimeout(resolve, 4000));

      // Generate PDF from the invisible modal
      const pdfData = await generateInvisiblePDF();
      
      // Close the invisible modal
      setIsBetaPaymentsModalOpen(false);
      setIsInvisiblePDFGeneration(false);

      if (!pdfData) {
        portalToast.dismiss(loadingToast);
        portalToast.error('Failed to generate PDF. Please try again.');
        return;
      }

      // Convert blob to base64
      const reader = new FileReader();
      const base64Promise = new Promise<string>((resolve, reject) => {
        reader.onloadend = () => {
          const base64String = reader.result as string;
          // Remove data:application/pdf;base64, prefix
          const base64Content = base64String.split(',')[1];
          resolve(base64Content);
        };
        reader.onerror = reject;
      });
      reader.readAsDataURL(pdfData.blob);
      
      const base64Content = await base64Promise;

      // Generate payment link if balance is owed and Stripe Connect is enabled
      let paymentLinkHtml = '';
      const currentTotal = booking?.total_amount || 0;
      const payments = booking?.payments || [];
      const validPayments = payments.filter((payment: any) => 
        payment.status === 'completed' || payment.status === 'succeeded' || payment.status === 'paid'
      );
      const totalPaid = validPayments.reduce((sum: number, payment: any) => sum + (payment.amount || 0), 0);
      const amountOwing = Math.max(0, currentTotal - totalPaid);

      if (amountOwing > 0 && allowStripePay && (selectedBusiness as any)?.stripe_connect_account_id && (selectedBusiness as any)?.online_payments_enabled !== false) {
        try {
          console.log('🔗 Generating payment link for invoice...');
          
          // Smart payment record management - prevent duplicates and reuse links
          let unpaidPayment = payments.find((p: any) => 
            p.status === 'pending' && 
            (p.amount === 0 || p.reference === 'unpaid-invoice')
          );
          
          let paymentUrl = null;
          
          if (!unpaidPayment) {
            console.log('🔍 No existing pending payment found, creating new one...');
            // Create unpaid invoice record
            const { data: newPayment, error: paymentError } = await supabase
              .from('payments')
              .insert({
                business_id: selectedBusiness?.id || '',
                booking_id: booking.id,
                amount: 0, // Placeholder for unpaid invoice
                method: 'credit_card',
                status: 'pending',
                reference: 'unpaid-invoice',
                notes: `Unpaid invoice for booking #${booking.booking_number}`,
                payment_token: crypto.randomUUID()
              })
              .select()
              .single();

            if (paymentError) {
              console.error('Failed to create unpaid payment record:', paymentError);
            } else {
              unpaidPayment = newPayment;
              console.log('✅ Created new unpaid payment record:', unpaidPayment?.id);
            }
          } else {
            console.log('✅ Found existing pending payment record:', unpaidPayment?.id);
          }

          if (unpaidPayment) {
            // Check if this payment already has a payment link
            const { data: existingLink, error: linkCheckError } = await supabase
              .from('payment_links')
              .select('payment_url')
              .eq('payment_id', unpaidPayment.id)
              .eq('status', 'active')
              .single();

            if (!linkCheckError && existingLink?.payment_url) {
              // Reuse existing payment link
              paymentUrl = existingLink.payment_url;
              console.log('♻️ Reusing existing payment link:', paymentUrl);
            } else {
              // Generate new payment link
              console.log('🔗 Generating new payment link...');
              const { data: linkData, error: linkError } = await supabase.functions.invoke('generate-invoice-payment-link', {
                body: { paymentId: unpaidPayment.id }
              });

              if (!linkError && linkData?.paymentUrl) {
                paymentUrl = linkData.paymentUrl;
                console.log('✅ New payment link generated:', paymentUrl);
              } else {
                console.error('❌ Failed to generate payment link:', linkError);
              }
            }

            if (paymentUrl) {
              paymentLinkHtml = `
                <div style="margin: 30px 0; padding: 25px; background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); border-radius: 12px; text-align: center;">
                  <h3 style="color: white; margin: 0 0 15px 0; font-size: 18px;">Pay Your Invoice Online</h3>
                  <p style="color: #e2e8f0; margin: 0 0 20px 0; font-size: 14px;">Secure payment with credit or debit card</p>
                  <a href="${paymentUrl}" 
                     style="display: inline-block; background: #ffffff; color: #667eea; padding: 12px 30px; text-decoration: none; border-radius: 8px; font-weight: bold; font-size: 16px; box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);">
                    Pay $${amountOwing.toFixed(2)} Now
                  </a>
                  <p style="color: #cbd5e0; margin: 15px 0 0 0; font-size: 12px;">Powered by Stripe - Secure &amp; Encrypted</p>
                </div>
              `;
            }
          }
        } catch (paymentLinkError) {
          console.error('Error generating payment link:', paymentLinkError);
          // Continue without payment link
        }
      }

      // Create email body
      const emailBody = `
        <div style="font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; max-width: 600px; margin: 0 auto; background: #ffffff;">
          <div style="background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); padding: 30px; text-align: center; border-radius: 12px 12px 0 0;">
            <h1 style="color: white; margin: 0; font-size: 28px; font-weight: bold;">Invoice from ${selectedBusiness?.name || 'Business'}</h1>
            <p style="color: #e2e8f0; margin: 10px 0 0 0; font-size: 16px;">Booking #${booking.booking_number}</p>
          </div>
          
          <div style="padding: 30px; background: #f8fafc;">
            <p style="color: #374151; font-size: 16px; line-height: 1.6; margin: 0 0 20px 0;">
              Hi ${booking.customer?.first_name || 'there'},
            </p>
            
            <p style="color: #374151; font-size: 16px; line-height: 1.6; margin: 0 0 20px 0;">
              Thank you for choosing ${selectedBusiness?.name || 'our business'}! Please find your invoice details below.
            </p>
            
            ${amountOwing > 0 ? `
              <p style="color: #374151; font-size: 16px; line-height: 1.6; margin: 0 0 20px 0;">
                <strong>Amount Due: $${amountOwing.toFixed(2)}</strong>
              </p>
            ` : `
              <p style="color: #059669; font-size: 16px; line-height: 1.6; margin: 0 0 20px 0; font-weight: bold;">
                ✅ This invoice has been paid in full. Thank you!
              </p>
            `}
            
            ${paymentLinkHtml}
            
            <div style="margin: 20px 0; padding: 20px; background: #ffffff; border: 1px solid #e5e7eb; border-radius: 8px;">
              <h3 style="margin: 0 0 15px 0; color: #374151;">Invoice Summary</h3>
              <div style="display: flex; justify-content: space-between; margin-bottom: 10px;">
                <span style="color: #6b7280;">Subtotal:</span>
                <span style="color: #374151; font-weight: bold;">$${(booking.subtotal || 0).toFixed(2)}</span>
              </div>
              <div style="display: flex; justify-content: space-between; margin-bottom: 10px;">
                <span style="color: #6b7280;">GST (5%):</span>
                <span style="color: #374151; font-weight: bold;">$${(booking.tax_amount || 0).toFixed(2)}</span>
              </div>
              <div style="display: flex; justify-content: space-between; padding-top: 10px; border-top: 1px solid #e5e7eb;">
                <span style="color: #374151; font-weight: bold;">Total:</span>
                <span style="color: #374151; font-weight: bold;">$${currentTotal.toFixed(2)}</span>
              </div>
            </div>
            
            <p style="color: #6b7280; font-size: 14px; line-height: 1.6; margin: 20px 0 0 0;">
              If you have any questions about this invoice, please don't hesitate to contact us.
            </p>
          </div>
          
          <div style="background: #f8fafc; padding: 20px; text-align: center; border-radius: 0 0 12px 12px; border-top: 1px solid #e5e7eb;">
            <p style="color: #6b7280; font-size: 14px; margin: 0;">This is an automated invoice from ${selectedBusiness?.name || 'Business'}</p>
          </div>
        </div>
      `;
      
      // Send email with PDF attachment
      const result = await sendGmailEmail({
        to: booking.customer?.email || '',
        subject: `Invoice #${booking.booking_number} from ${selectedBusiness?.name || 'Business'}${amountOwing > 0 ? ' - Pay Online' : ' - Thank You'}`,
        body: emailBody,
        businessId: selectedBusiness?.id || '',
        attachments: [{
          filename: pdfData.filename,
          content: base64Content,
          type: 'application/pdf'
        }]
      });
      
      if (result.success) {
        console.log('✅ Invoice sent successfully!');
        portalToast.dismiss(loadingToast);
        portalToast.success('Invoice sent successfully! ✅', {
          duration: 4000,
          position: 'top-center'
        });
        
        // Refresh the booking data to show updated invoice sent status
        if (onUpdate) {
          try {
            const result = onUpdate();
            if (result instanceof Promise) {
              await result;
            }
            console.log('✅ Booking data refreshed after invoice send');
          } catch (error) {
            console.error('❌ Failed to refresh booking data:', error);
          }
        }
      } else {
        console.error('❌ Failed to send invoice:', result.error);
        portalToast.dismiss(loadingToast);
        portalToast.error('Failed to send invoice. Please try again.', {
          duration: 4000,
          position: 'top-center'
        });
      }
      
    } catch (error) {
      console.error('❌ Error sending invoice:', error);
      portalToast.dismiss(loadingToast);
      portalToast.error('Failed to send invoice. Please try again.', {
        duration: 4000,
        position: 'top-center'
      });
    }
  };

  // Generate PDF from the invisible BetaPaymentsModal
  const generateInvisiblePDF = async (): Promise<{ blob: Blob; filename: string } | null> => {
    try {
      console.log('🔍 Starting PDF generation...');
      
      // Import libraries dynamically
      const [{ default: html2canvas }, { default: jsPDF }] = await Promise.all([
        import('html2canvas'),
        import('jspdf')
      ]);
      
      // Find the invoice preview element with more specific selectors
      let invoiceElement = document.querySelector('.invoice-preview') as HTMLElement;
      
      // If not found, try alternative selectors
      if (!invoiceElement) {
        console.log('🔍 .invoice-preview not found, trying alternative selectors...');
        invoiceElement = document.querySelector('[class*="invoice"]') as HTMLElement;
      }
      
      if (!invoiceElement) {
        console.error('❌ Invoice preview element not found with any selector');
        console.log('Available elements:', document.querySelectorAll('*[class*="invoice"]'));
        return null;
      }

      console.log('✅ Found invoice element:', invoiceElement);
      console.log('📏 Element dimensions:', {
        width: invoiceElement.offsetWidth,
        height: invoiceElement.offsetHeight,
        visible: invoiceElement.offsetParent !== null
      });

      // Check if element has content
      if (invoiceElement.offsetWidth === 0 || invoiceElement.offsetHeight === 0) {
        console.error('❌ Invoice element has no dimensions - likely not rendered');
        return null;
      }

      console.log('📸 Converting to canvas...');
      
      // Convert to canvas with better options
      const canvas = await html2canvas(invoiceElement, {
        scale: 2, // Higher resolution
        useCORS: true,
        allowTaint: true,
        backgroundColor: '#ffffff',
        logging: true, // Enable logging for debugging
        width: invoiceElement.scrollWidth,
        height: invoiceElement.scrollHeight
      });
      
      console.log('✅ Canvas created:', {
        width: canvas.width,
        height: canvas.height
      });

      // Check if canvas is empty
      if (canvas.width === 0 || canvas.height === 0) {
        console.error('❌ Canvas has no dimensions');
        return null;
      }
      
      // Create PDF
      const imgData = canvas.toDataURL('image/png');
      console.log('📄 Creating PDF...');
      
      const pdf = new jsPDF('p', 'mm', 'a4');
      
      // Calculate dimensions to fit A4
      const imgWidth = 210; // A4 width in mm
      const pageHeight = 295; // A4 height in mm
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      let heightLeft = imgHeight;
      
      let position = 0;
      
      // Add first page
      pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;
      
      // Add additional pages if needed
      while (heightLeft >= 0) {
        position = heightLeft - imgHeight;
        pdf.addPage();
        pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;
      }
      
      // Generate filename
      const invoiceNumber = booking?.booking_number || 0;
      const filename = `invoice-${invoiceNumber}.pdf`;
      
      // Get PDF as blob
      const blob = pdf.output('blob');
      
      console.log('✅ PDF generated successfully:', {
        filename,
        size: blob.size
      });
      
      return { blob, filename };
    } catch (error) {
      console.error('❌ Error generating PDF:', error);
      return null;
    }
  };

  const handleViewInvoice = async () => {
    try {
      setIsLoadingInvoice(true);
      
      // Always fetch fresh booking data to ensure complete service details
      const freshBooking = await getBooking(booking.id);
      if (!freshBooking) {
        throw new Error('Could not load booking details');
      }

      // Create all payment details objects for the modal
      const allPayments: any[] = [];

      // Add existing payments with fresh booking data
      const existingPayments = (booking.payments || []).map(payment => {
        return {
          id: payment.id,
          business_id: freshBooking.business_id,
          booking_id: freshBooking.id,
          amount: payment.amount,
          status: payment.status as 'pending' | 'completed' | 'voided',
          created_at: payment.created_at,
          notes: undefined,
          booking_number: typeof freshBooking.booking_number === 'number' ? freshBooking.booking_number : parseInt(String(freshBooking.booking_number)) || 0,
          booking_title: freshBooking.title,
          booking_subtotal: freshBooking.subtotal,
          customer_first_name: freshBooking.customer?.first_name || '',
          customer_last_name: freshBooking.customer?.last_name || '',
          customer_email: freshBooking.customer?.email || '',
          booking_services: freshBooking.services?.map(s => ({
            id: s.id,
            name: s.name,
            description: s.description,
            price: s.price,
            tax_amount: s.tax_amount,
            total_amount: s.total_amount,
            duration_minutes: s.duration_minutes,
            vehicle_make: s.vehicle_make || '',
            vehicle_model: s.vehicle_model || ''
          })) || [],
          booking_discounts: freshBooking.discounts?.map(d => ({
            id: d.id,
            title: d.title,
            type: d.type as 'percentage' | 'fixed',
            amount: d.amount
          })) || []
        };
      });

      allPayments.push(...existingPayments);

      // If no payments exist, create a pending invoice
      if (!booking.payments || booking.payments.length === 0) {
        const pendingInvoice = {
          id: 'pending',
          business_id: freshBooking.business_id,
          booking_id: freshBooking.id,
          amount: freshBooking.total_amount || 0,
          status: 'pending' as const,
          created_at: new Date().toISOString(),
          notes: undefined,
          booking_number: typeof freshBooking.booking_number === 'number' ? freshBooking.booking_number : parseInt(String(freshBooking.booking_number)) || 0,
          booking_title: freshBooking.title,
          booking_subtotal: freshBooking.subtotal,
          customer_first_name: freshBooking.customer?.first_name || '',
          customer_last_name: freshBooking.customer?.last_name || '',
          customer_email: freshBooking.customer?.email || '',
          booking_services: freshBooking.services?.map(s => ({
            id: s.id,
            name: s.name,
            description: s.description,
            price: s.price,
            tax_amount: s.tax_amount,
            total_amount: s.total_amount,
            duration_minutes: s.duration_minutes,
            vehicle_make: s.vehicle_make || '',
            vehicle_model: s.vehicle_model || ''
          })) || [],
          booking_discounts: freshBooking.discounts?.map(d => ({
            id: d.id,
            title: d.title,
            type: d.type as 'percentage' | 'fixed',
            amount: d.amount
          })) || []
        };
        allPayments.push(pendingInvoice);
      } else {
        // Check if there's a remaining balance for partial payments
        const totalPaid = (booking.payments || [])
          .filter(p => p.status === 'completed')
          .reduce((sum, p) => sum + p.amount, 0);
        
        const remainingBalance = (freshBooking.total_amount || 0) - totalPaid;
        
        if (remainingBalance > 0) {
          // Create an unpaid invoice for the remaining balance
          const remainingInvoice = {
            id: 'pending-balance',
            business_id: freshBooking.business_id,
            booking_id: freshBooking.id,
            amount: remainingBalance,
            status: 'pending' as const,
            created_at: new Date().toISOString(),
            notes: undefined,
            booking_number: typeof freshBooking.booking_number === 'number' ? freshBooking.booking_number : parseInt(String(freshBooking.booking_number)) || 0,
            booking_title: freshBooking.title,
            booking_subtotal: freshBooking.subtotal,
            customer_first_name: freshBooking.customer?.first_name || '',
            customer_last_name: freshBooking.customer?.last_name || '',
            customer_email: freshBooking.customer?.email || '',
            booking_services: freshBooking.services?.map(s => ({
              id: s.id,
              name: s.name,
              description: s.description,
              price: s.price,
              tax_amount: s.tax_amount,
              total_amount: s.total_amount,
              duration_minutes: s.duration_minutes,
              vehicle_make: s.vehicle_make || '',
              vehicle_model: s.vehicle_model || ''
            })) || [],
            booking_discounts: freshBooking.discounts?.map(d => ({
              id: d.id,
              title: d.title,
              type: d.type as 'percentage' | 'fixed',
              amount: d.amount
            })) || []
          };
          allPayments.push(remainingInvoice);
        }
      }
      
      // Set selectedPayment to trigger PaymentDetailsModal to open
      setSelectedPayment(allPayments[0] || null);
      setIsPaymentDetailsModalOpen(true);

      // Store allPayments in a state for the modal
      setAllPaymentDetails(allPayments);
    } catch (err) {
      console.error('Error loading booking for invoice:', err);
      onError('Failed to load invoice details');
    } finally {
      setIsLoadingInvoice(false);
    }
  };

  const handleSaveTime = async () => {
    try {
      // Use onScheduleChange if available (for automation cancel/retrigger logic)
      if (onScheduleChange) {
        await onScheduleChange(
          timeFormData.drop_off_date,
          timeFormData.drop_off_time,
          timeFormData.start_date,
          timeFormData.start_time,
          timeFormData.end_date,
          timeFormData.end_time
        );
      } else {
        // Fallback to direct update
        await updateBooking(booking.id, timeFormData);
        await onUpdate();
      }
      setIsEditingTime(false);
    } catch (err) {
      console.error('Error updating times:', err);
      onError(err instanceof Error ? err.message : 'Failed to update times');
    }
  };

  const handleSaveCustomerNotes = async (notes: string) => {
    try {
      await updateBooking(booking.id, { customer_notes: notes });
      setCustomerNotes(notes);
      await onUpdate();
    } catch (err) {
      console.error('Error updating customer notes:', err);
      onError(err instanceof Error ? err.message : 'Failed to update customer notes');
    }
  };

  const handleColorChange = async (color: string) => {
    try {
      await updateBooking(booking.id, { color });
      await onUpdate();
    } catch (err) {
      console.error('Error changing color:', err);
      onError(err instanceof Error ? err.message : 'Failed to change color');
    }
  };

  const handleSourceUpdate = async (sourceName: string | null) => {
    if (!booking.id) return;
    
    try {
      await updateBooking(booking.id, {
        source: sourceName || undefined
      });
      
      // Update local booking state
      onUpdate?.();
    } catch (error) {
      console.error('Error updating booking source:', error);
      onError?.(error instanceof Error ? error.message : 'Failed to update booking source');
    }
  };

  const handleRecordPayment = () => {
    setIsRecordPaymentModalOpen(true);
  };


  const handleSendLiabilityWaiver = () => {
    setIsSendLiabilityWaiverModalOpen(true);
  };

  const handleSendLiabilityWaiverSMS = async () => {
    if (!booking.customer?.phone) {
      onError('Customer phone number is required to send liability waiver');
      setIsSendLiabilityWaiverModalOpen(false);
      return;
    }

    try {
      setIsSendingLiabilityWaiver(true);
      
      const customerName = `${booking.customer.first_name || ''} ${booking.customer.last_name || ''}`.trim();
      const businessName = selectedBusiness?.name || 'Your Service Provider';
      
      const success = await sendLiabilityWaiverSMS(
        booking.id,
        booking.customer.phone,
        customerName,
        businessName
      );
      
      if (success) {
        // Reload waiver data to get the updated token
        const updatedWaiver = await getLiabilityWaiverByBookingId(booking.id);
        setLiabilityWaiver(updatedWaiver);
        
        // Show success message
        console.log('Liability waiver SMS sent successfully');
        setIsSendLiabilityWaiverModalOpen(false);
      }
    } catch (error) {
      console.error('Error sending liability waiver SMS:', error);
      onError('Failed to send liability waiver SMS');
    } finally {
      setIsSendingLiabilityWaiver(false);
    }
  };

  const handleSendLiabilityWaiverEmail = async () => {
    if (!booking.customer?.email) {
      onError('Customer email address is required to send liability waiver');
      setIsSendLiabilityWaiverModalOpen(false);
      return;
    }

    try {
      setIsSendingLiabilityWaiver(true);
      
      const customerName = `${booking.customer.first_name || ''} ${booking.customer.last_name || ''}`.trim();
      const businessName = selectedBusiness?.name || 'Your Service Provider';
      
      const success = await sendLiabilityWaiverEmail(
        booking.id,
        booking.customer.email,
        customerName,
        businessName
      );
      
      if (success) {
        // Reload waiver data to get the updated token
        const updatedWaiver = await getLiabilityWaiverByBookingId(booking.id);
        setLiabilityWaiver(updatedWaiver);
        
        // Show success message
        console.log('Liability waiver email sent successfully');
        setIsSendLiabilityWaiverModalOpen(false);
      }
    } catch (error) {
      console.error('Error sending liability waiver email:', error);
      onError('Failed to send liability waiver email');
    } finally {
      setIsSendingLiabilityWaiver(false);
    }
  };

  const handleSignLiabilityWaiverOnDevice = async () => {
    try {
      setIsSendingLiabilityWaiver(true);
      
      // Detect mobile/PWA environment
      const isMobile = window.innerWidth < 768;
      const isPWA = window.matchMedia('(display-mode: standalone)').matches || 
                    (window.navigator as any).standalone || 
                    document.referrer.includes('android-app://');
      
      // Generate or get existing waiver token
      const token = await generateLiabilityWaiver(booking.id);
      if (!token) {
        throw new Error('Failed to generate liability waiver token');
      }

      // Generate waiver URL
      const waiverURL = generateLiabilityWaiverURL(token);
      
      // Use different approaches based on environment
      if (isMobile || isPWA) {
        // For mobile/PWA: Redirect in same window to avoid popup blockers
        console.log('🚗 [WAIVER] Mobile/PWA detected, redirecting in same window');
        window.location.href = waiverURL;
      } else {
        // For desktop: Open in new tab
        console.log('🚗 [WAIVER] Desktop detected, opening in new tab');
        window.open(waiverURL, '_blank');
      }
      
      setIsSendLiabilityWaiverModalOpen(false);
    } catch (err) {
      console.error('Error opening liability waiver on device:', err);
      onError(err instanceof Error ? err.message : 'Failed to open liability waiver');
    } finally {
      setIsSendingLiabilityWaiver(false);
    }
  };

  const handleAddDamageReport = () => {
    setIsDamageReportModalOpen(true);
  };

  const handleDamageReportSave = (savedReport: VehicleDamageReport) => {
    setDamageReport(savedReport);
    setIsDamageReportModalOpen(false);
  };

  // Notify parent of damage/waiver state changes
  useEffect(() => {
    if (onDamageWaiverStateChange) {
      onDamageWaiverStateChange({
        isLiabilityWaiverEnabled: isLiabilityWaiverEnabledForBusiness,
        liabilityWaiverAgreed: !!liabilityWaiver?.agreed_at,
        hasDamageReport: !!damageReport,
        liabilityWaiverPdfUrl: liabilityWaiver?.pdf_url,
        onSendLiabilityWaiver: handleSendLiabilityWaiver,
        onAddDamageReport: handleAddDamageReport,
        isSendingLiabilityWaiver
      });
    }
  }, [
    isLiabilityWaiverEnabledForBusiness,
    liabilityWaiver,
    damageReport,
    isSendingLiabilityWaiver,
    onDamageWaiverStateChange
  ]);









  if (activeTab === 'overview') {
    const isMobile = window.innerWidth < 640;
    
    // Status management - excluding 'created' from display
    const statuses = [
      { value: 'scheduled', label: 'Scheduled' },
      { value: 'in_progress', label: 'In Progress' },
      { value: 'completed', label: 'Completed' }
    ];
    
    return (
      <div className="space-y-1.5 sm:space-y-6">
        {/* Status Tracker Card - Mobile Only */}
        {isMobile && onStatusChange && (
          <div className="bg-white rounded-lg border border-gray-200 overflow-hidden shadow-sm">
            <div className="p-4">
              {booking.status === 'cancelled' ? (
                <div className="flex items-center justify-center space-x-2">
                  <XCircle className="h-4 w-4 text-red-400" />
                  <span className="text-red-400 text-sm font-medium">Booking Cancelled</span>
                </div>
              ) : (
                <div className="flex items-center justify-center">
                  <div className="flex items-center space-x-1 text-xs overflow-x-auto scrollbar-hide">
                    {statuses.map((s, index) => {
                      const currentIndex = statuses.findIndex(st => st.value === booking.status);
                      return (
                        <React.Fragment key={s.value}>
                          <button
                            onClick={() => {
                              if (index <= currentIndex + 1 && onStatusChange) {
                                onStatusChange(s.value);
                              }
                            }}
                            disabled={index > currentIndex + 1}
                            className={`font-bold transition-all duration-200 px-3 py-2 rounded-lg whitespace-nowrap ${
                              index <= currentIndex
                                ? 'text-gray-700 bg-gray-100 shadow-sm'
                                : index === currentIndex + 1
                                ? 'text-gray-500 hover:text-gray-700 hover:bg-gray-100 active:scale-[0.98]'
                                : 'text-gray-400 cursor-not-allowed'
                            }`}
                            style={{ touchAction: 'manipulation' }}
                          >
                            {s.label}
                          </button>
                          {index < statuses.length - 1 && (
                            <ChevronRight className={`h-3 w-3 ${
                              index < currentIndex ? 'text-gray-600' : 'text-gray-300'
                            }`} />
                          )}
                        </React.Fragment>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Warranty Information Section - Only for Warranty Bookings */}
        {booking.is_warranty && (
          <div className="bg-white rounded-lg border border-gray-200 overflow-hidden shadow-sm">
            <div className="p-6">
              <div className="flex items-center mb-2">
                <h3 className="text-xs sm:text-sm font-bold text-gray-900">
                  Warranty Information
                </h3>
              </div>
              <div className="space-y-2">

                
                {booking.original_booking_id && (
                  <div className="space-y-2">
                    <div className="text-xs font-medium text-gray-700 mb-1">
                      Warranty for:
                    </div>
                    <div className="bg-gray-50/80 rounded-lg p-2 sm:p-3 border border-gray-200/30">
                      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between text-xs sm:text-sm space-y-2 sm:space-y-0">
                        <div className="flex flex-col sm:flex-row sm:items-center space-y-1 sm:space-y-0 sm:space-x-4 lg:space-x-6">
                          <div className="flex items-center space-x-1.5 sm:space-x-2">
                            <span className="font-medium text-gray-700">Booking #:</span>
                            <span className="text-gray-900 font-mono">
                              {originalBooking?.booking_number || booking.original_booking_id.slice(-8)}
                            </span>
                          </div>
                          <div className="flex items-center space-x-1.5 sm:space-x-2">
                            <span className="font-medium text-gray-700">Completed:</span>
                            <span className="text-gray-900">
                              {originalBooking?.completed_at ? 
                                new Date(originalBooking.completed_at).toLocaleDateString() + ' ' + 
                                new Date(originalBooking.completed_at).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'}) 
                                : 'Loading...'}
                            </span>
                          </div>
                        </div>
                        <div className="flex items-center space-x-1.5 sm:space-x-2">
                          <span className="font-medium text-gray-700">Services:</span>
                          <button 
                            onClick={() => setShowServiceDetails(!showServiceDetails)}
                            className="flex items-center text-gray-900 hover:text-purple-600 transition-colors text-xs sm:text-sm"
                          >
                            <span className="font-medium">{originalBookingServices.length} service(s)</span>
                            <ChevronDown className={`h-3 w-3 sm:h-4 sm:w-4 ml-1 sm:ml-1.5 transition-transform ${showServiceDetails ? 'rotate-180' : ''}`} />
                          </button>
                        </div>
                      </div>
                        {showServiceDetails && originalBookingServices.length > 0 && (
                          <div className="mt-2 sm:mt-3 space-y-1.5 sm:space-y-2">
                            {originalBookingServices.map((service, index) => (
                              <div key={index} className="bg-white/80 rounded-lg p-2 sm:p-2.5 border border-gray-200/50">
                                <div className="font-semibold text-gray-800 text-xs sm:text-sm">{service.name}</div>
                                {service.description && (
                                  <div className="text-gray-600 text-xs mt-0.5 sm:mt-1 leading-relaxed">{service.description}</div>
                                )}
                                <div className="text-gray-700 font-medium mt-1 sm:mt-1.5 text-xs sm:text-sm">${service.total_amount?.toFixed(2) || service.price?.toFixed(2) || '0.00'}</div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                )}
                

              </div>
            </div>
          </div>
        )}

        {/* Date and Time Information */}
        <div className="bg-white rounded-lg border border-gray-200 shadow-sm">
          <div className="p-6">
            {isEditingTime && canAccess.editBookingDetails ? (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Drop-off Date</label>
                    <input
                      type="date"
                      value={timeFormData.drop_off_date}
                      onChange={(e) => setTimeFormData(prev => ({ ...prev, drop_off_date: e.target.value }))}
                      className="w-full h-8 px-2 text-xs border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Drop-off Time</label>
                    <input
                      type="time"
                      value={timeFormData.drop_off_time}
                      onChange={(e) => setTimeFormData(prev => ({ ...prev, drop_off_time: e.target.value }))}
                      className="w-full h-8 px-2 text-xs border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Start Date</label>
                    <input
                      type="date"
                      value={timeFormData.start_date}
                      onChange={(e) => setTimeFormData(prev => ({ ...prev, start_date: e.target.value }))}
                      className="w-full h-8 px-2 text-xs border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Start Time</label>
                    <input
                      type="time"
                      value={timeFormData.start_time}
                      onChange={(e) => setTimeFormData(prev => ({ ...prev, start_time: e.target.value }))}
                      className="w-full h-8 px-2 text-xs border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">End Date</label>
                    <input
                      type="date"
                      value={timeFormData.end_date}
                      onChange={(e) => setTimeFormData(prev => ({ ...prev, end_date: e.target.value }))}
                      className="w-full h-8 px-2 text-xs border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">End Time</label>
                    <input
                      type="time"
                      value={timeFormData.end_time}
                      onChange={(e) => setTimeFormData(prev => ({ ...prev, end_time: e.target.value }))}
                      className="w-full h-8 px-2 text-xs border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                </div>
                <div className="flex justify-end">
                  <button
                    onClick={handleSaveTime}
                    className="w-full px-4 py-2 text-sm font-semibold text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors shadow-sm"
                  >
                    Save Changes
                  </button>
                </div>
              </div>
            ) : (
              <ScheduleTimeline
                booking={booking}
                isEditing={isEditingTime}
                onEdit={() => setIsEditingTime(true)}
                canEdit={canAccess.editBookingDetails && !booking.mobile_booking}
                isMobile={window.innerWidth < 640}
                onStatusChange={onStatusChange}
                readOnly={booking.status === 'cancelled'}
                onRescheduleMobileBooking={onRescheduleMobileBooking}
              />
            )}
          </div>
        </div>

        {/* Customer Feedback Section - Only show for completed bookings */}
        {booking.status === 'completed' && (
          <div className="bg-white rounded-lg border border-gray-200 overflow-hidden shadow-sm">
            <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
              <div className="flex items-center justify-between">
                <h3 className="text-xs sm:text-base font-bold text-gray-900 flex items-center">
                  <CheckCircle className="h-3 w-3 sm:h-5 sm:w-5 mr-1.5 sm:mr-2 text-green-600" />
                  Customer Feedback
                </h3>
                {bookingFeedback && canAccess.editBookingDetails && (
                  <div className="flex items-center space-x-2">
                    {isEditingFeedback ? (
                      <>
                        <button
                          onClick={handleSaveFeedback}
                          disabled={isSavingFeedback}
                          className="flex items-center px-2 py-1 text-xs font-medium text-white bg-green-600 rounded hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        >
                          <Save className="h-3 w-3 mr-1" />
                          {isSavingFeedback ? 'Saving...' : 'Save'}
                        </button>
                        <button
                          onClick={handleCancelEditingFeedback}
                          disabled={isSavingFeedback}
                          className="flex items-center px-2 py-1 text-xs font-medium text-gray-600 bg-gray-100 rounded hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        >
                          <XCircle className="h-3 w-3 mr-1" />
                          Cancel
                        </button>
                      </>
                    ) : (
                      <button
                        onClick={handleStartEditingFeedback}
                        className="flex items-center px-2 py-1 text-xs font-medium text-gray-600 bg-gray-100 rounded hover:bg-gray-200 transition-colors"
                      >
                        <Edit3 className="h-3 w-3 mr-1" />
                        Edit Ratings
                      </button>
                    )}
                  </div>
                )}
              </div>
            </div>
            <div className="p-2 sm:p-6">
              {!isFeedbackEnabledForBusiness ? (
                // Show disabled message when feedback is turned off
                <div className="text-center py-4">
                  <div className="text-xs text-gray-500 bg-gray-50 p-3 rounded-md border">
                    Customer Feedback is disabled
                  </div>
                </div>
              ) : bookingFeedback ? (
                // Show feedback results when available and enabled
                <div className="space-y-3">
                  <div className="text-xs text-gray-600">
                    Submitted {format(new Date(bookingFeedback.submitted_at), 'MMM d, yyyy')}
                  </div>

                  {/* Dynamic Overall Rating Calculation */}
                  {(() => {
                    // Get responses from form_responses or fallback to legacy fields
                    const responses = (bookingFeedback as ExtendedBookingFeedback).form_responses || {};
                    const ratingFields = feedbackFormFields.filter(field => field.field_type === 'rating');
                    
                    let totalRating = 0;
                    let ratingCount = 0;
                    
                    // Calculate overall rating from dynamic responses
                    if (Object.keys(responses).length > 0) {
                      ratingFields.forEach(field => {
                        const value = responses[field.field_key];
                        if (typeof value === 'number' && value > 0) {
                          totalRating += value;
                          ratingCount++;
                        }
                      });
                    } else {
                      // Fallback to legacy fields for backward compatibility
                      if (bookingFeedback.overall_quality_rating) {
                        totalRating += bookingFeedback.overall_quality_rating;
                        ratingCount++;
                      }
                      if (bookingFeedback.overall_experience_rating) {
                        totalRating += bookingFeedback.overall_experience_rating;
                        ratingCount++;
                      }
                    }
                    
                    const overallRating = ratingCount > 0 ? totalRating / ratingCount : 0;

                    return (
                      /* Overall Rating - Compact Single Line with Dropdown */
                      <div className="bg-gray-50 rounded-md p-2">
                        <button
                          onClick={() => setShowIndividualRatings(!showIndividualRatings)}
                          className="flex items-center justify-between w-full text-left hover:bg-gray-100 rounded transition-colors"
                        >
                          <div className="text-xs font-medium text-gray-700">Overall Rating</div>
                          <div className="flex items-center space-x-2">
                            <StarRating rating={Math.round(overallRating)} size="small" />
                            <span className="text-sm font-bold text-gray-900">
                              {overallRating.toFixed(1)}
                            </span>
                            {showIndividualRatings ? (
                              <ChevronDown className="h-3 w-3 text-gray-600" />
                            ) : (
                              <ChevronRight className="h-3 w-3 text-gray-600" />
                            )}
                          </div>
                        </button>
                        
                        {/* Individual Ratings - Dynamic Dropdown Content */}
                        {showIndividualRatings && (
                          <div className="mt-2 pt-2 border-t border-gray-200 space-y-2">
                            {/* Show dynamic rating fields */}
                            {Object.keys(responses).length > 0 ? (
                              ratingFields.map(field => {
                                const value = responses[field.field_key];
                                if (typeof value === 'number' && value > 0) {
                                  const currentRating = isEditingFeedback 
                                    ? (editingFeedbackData.form_responses?.[field.field_key] as number) || value
                                    : value;
                                  
                                  return (
                                    <div key={field.id} className="flex items-center justify-between py-1.5 px-2 bg-white rounded text-xs">
                                      <span className="text-gray-700">{field.field_label}</span>
                                      {isEditingFeedback ? (
                                        <EditableStarRating
                                          rating={currentRating}
                                          onRatingChange={(newRating) => handleRatingChange(field.field_key, newRating)}
                                          size="small"
                                        />
                                      ) : (
                                        <div className="flex items-center space-x-1">
                                          <StarRating rating={value} size="small" />
                                          <span className="text-gray-600 w-6">{value}/5</span>
                                        </div>
                                      )}
                                    </div>
                                  );
                                }
                                return null;
                              })
                            ) : (
                              // Fallback to legacy display for old feedback
                              <>
                                {bookingFeedback.overall_quality_rating && (
                                  <div className="flex items-center justify-between py-1.5 px-2 bg-white rounded text-xs">
                                    <span className="text-gray-700">Quality</span>
                                    {isEditingFeedback ? (
                                      <EditableStarRating
                                        rating={editingFeedbackData.overall_quality_rating || bookingFeedback.overall_quality_rating}
                                        onRatingChange={(newRating) => handleRatingChange('overall_quality_rating', newRating)}
                                        size="small"
                                      />
                                    ) : (
                                      <div className="flex items-center space-x-1">
                                        <StarRating rating={bookingFeedback.overall_quality_rating} size="small" />
                                        <span className="text-gray-600 w-6">{bookingFeedback.overall_quality_rating}/5</span>
                                      </div>
                                    )}
                                  </div>
                                )}
                                {bookingFeedback.overall_experience_rating && (
                                  <div className="flex items-center justify-between py-1.5 px-2 bg-white rounded text-xs">
                                    <span className="text-gray-700">Experience</span>
                                    {isEditingFeedback ? (
                                      <EditableStarRating
                                        rating={editingFeedbackData.overall_experience_rating || bookingFeedback.overall_experience_rating}
                                        onRatingChange={(newRating) => handleRatingChange('overall_experience_rating', newRating)}
                                        size="small"
                                      />
                                    ) : (
                                      <div className="flex items-center space-x-1">
                                        <StarRating rating={bookingFeedback.overall_experience_rating} size="small" />
                                        <span className="text-gray-600 w-6">{bookingFeedback.overall_experience_rating}/5</span>
                                      </div>
                                    )}
                                  </div>
                                )}
                              </>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })()}

                  {/* Dynamic Text Responses */}
                  {(() => {
                    const responses = (bookingFeedback as ExtendedBookingFeedback).form_responses || {};
                    const textFields = feedbackFormFields.filter(field => field.field_type === 'text');
                    
                    // Show dynamic text responses
                    if (Object.keys(responses).length > 0) {
                      return textFields.map(field => {
                        const value = responses[field.field_key];
                        if (typeof value === 'string' && value.trim()) {
                          return (
                            <div key={field.id} className="space-y-1">
                              <div className="text-xs font-medium text-gray-600">{field.field_label}:</div>
                              <div className="text-xs text-gray-600 bg-gray-50 p-2 rounded italic">
                                "{value}"
                              </div>
                            </div>
                          );
                        }
                        return null;
                      });
                    } else {
                      // Fallback to legacy improvement suggestions
                      if (bookingFeedback.improvement_suggestions) {
                        return (
                          <div className="space-y-1">
                            <div className="text-xs font-medium text-gray-600">Suggestions:</div>
                            <div className="text-xs text-gray-600 bg-gray-50 p-2 rounded italic">
                              "{bookingFeedback.improvement_suggestions}"
                            </div>
                          </div>
                        );
                      }
                    }
                    return null;
                  })()}

                  {/* Feedback Link Section - Collapsible */}
                  {feedbackLink && (
                    <div className="border-t border-gray-200 pt-2">
                      <button
                        onClick={() => setShowFeedbackLink(!showFeedbackLink)}
                        className="flex items-center justify-between w-full text-left py-1 text-xs font-medium text-gray-600 hover:text-gray-800 transition-colors"
                      >
                        <span>Share link</span>
                        {showFeedbackLink ? (
                          <ChevronDown className="h-3 w-3" />
                        ) : (
                          <ChevronRight className="h-3 w-3" />
                        )}
                      </button>
                      
                      {showFeedbackLink && (
                        <div className="mt-1 space-y-1">
                          <div className="flex items-center space-x-1 p-2 bg-gray-50 rounded border text-xs">
                            <input
                              type="text"
                              value={feedbackLink}
                              readOnly
                              className="flex-1 bg-transparent border-none focus:outline-none text-gray-700"
                            />
                            <button
                              onClick={handleCopyFeedbackLink}
                              className="flex items-center px-2 py-1 font-medium text-gray-700 bg-white border border-gray-300 rounded hover:bg-gray-50 transition-colors"
                            >
                              {feedbackCopied ? (
                                <>
                                  <CheckCircle className="h-3 w-3 mr-1 text-green-600" />
                                  Copied
                                </>
                              ) : (
                                <>
                                  <Copy className="h-3 w-3 mr-1" />
                                  Copy
                                </>
                              )}
                            </button>
                            <a
                              href={feedbackLink}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="flex items-center px-2 py-1 font-medium text-blue-700 bg-blue-50 border border-blue-200 rounded hover:bg-blue-100 transition-colors"
                            >
                              <ExternalLink className="h-3 w-3 mr-1" />
                              Preview
                            </a>
                          </div>
                          <p className="text-xs text-gray-500">
                            💡 Use <code className="bg-gray-200 px-1 rounded">[[feedback_link]]</code> in SMS automations
                          </p>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ) : (
                // Show no feedback message and link generation when enabled but no feedback yet
                <div className="space-y-2">
                  <p className="text-xs text-gray-600">No feedback yet.</p>
                  
                  {/* Show feedback link for sharing - Collapsible */}
                  {feedbackLink && (
                    <div>
                      <button
                        onClick={() => setShowFeedbackLink(!showFeedbackLink)}
                        className="flex items-center justify-between w-full text-left py-1 text-xs font-medium text-gray-600 hover:text-gray-800 transition-colors border-t border-gray-200"
                      >
                        <span>Share link to collect feedback</span>
                        {showFeedbackLink ? (
                          <ChevronDown className="h-3 w-3" />
                        ) : (
                          <ChevronRight className="h-3 w-3" />
                        )}
                      </button>
                      
                      {showFeedbackLink && (
                        <div className="mt-1 space-y-1">
                          <div className="flex items-center space-x-1 p-2 bg-gray-50 rounded border text-xs">
                            <input
                              type="text"
                              value={feedbackLink}
                              readOnly
                              className="flex-1 bg-transparent border-none focus:outline-none text-gray-700"
                            />
                            <button
                              onClick={handleCopyFeedbackLink}
                              className="flex items-center px-2 py-1 font-medium text-gray-700 bg-white border border-gray-300 rounded hover:bg-gray-50 transition-colors"
                            >
                              {feedbackCopied ? (
                                <>
                                  <CheckCircle className="h-3 w-3 mr-1 text-green-600" />
                                  Copied
                                </>
                              ) : (
                                <>
                                  <Copy className="h-3 w-3 mr-1" />
                                  Copy
                                </>
                              )}
                            </button>
                            <a
                              href={feedbackLink}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="flex items-center px-2 py-1 font-medium text-blue-700 bg-blue-50 border border-blue-200 rounded hover:bg-blue-100 transition-colors"
                            >
                              <ExternalLink className="h-3 w-3 mr-1" />
                              Preview
                            </a>
                          </div>
                          <p className="text-xs text-gray-500">
                            💡 Use <code className="bg-gray-200 px-1 rounded">[[feedback_link]]</code> in SMS automations
                          </p>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Generate link button if no link exists and feedback is enabled */}
                  {!feedbackLink && (
                    <button
                      onClick={handleGenerateFeedbackLink}
                      disabled={isGeneratingFeedback}
                      className="w-full px-3 py-1.5 text-xs font-medium text-white bg-green-600 rounded hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      {isGeneratingFeedback ? 'Generating...' : 'Generate Feedback Link'}
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>
        )}



        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden shadow-sm">
          <div className="p-6">
            <BookingCustomerInfo
              customer={{
                id: booking.customer?.id || '',
                first_name: booking.customer?.first_name || '',
                last_name: booking.customer?.last_name || '',
                email: booking.customer?.email || null,
                phone: booking.customer?.phone || '',
                business_id: booking.business_id,
                type: 'person',
                address: null,
                source: null,
                tags: [],
                last_interaction: null,
                lifetime_value: 0,
                next_service_date: null,
                referral_source: null,
                preferences: {},
                status: 'active',
                amount_spent: 0,
                credit_balance: 0,
                notes: null,
                tax_exempt: false,
                workflow_exempt: false,
                carfax_exempt: false,
                receive_reminders: true,
                receive_offers: true,
                lat: null,
                lng: null,
                is_supplier: false,
                repeat: false,
                created_at: booking.created_at,
                updated_at: booking.updated_at || booking.created_at,
                vehicles: booking.customer?.vehicles?.map(v => ({
                  ...v,
                  mileage: null,
                  notes: null,
                  last_service: null
                }))
              }}
              onEdit={canAccess.editBookingDetails ? onEditCustomer : undefined}
              bookingAddress={booking.booking_address || ''}
              onEditBookingAddress={canAccess.editBookingDetails ? onAddressUpdate : undefined}
              mobile_booking={booking.mobile_booking || false}
              canEdit={canAccess.editBookingDetails}
              customerNotes={booking.customer_notes}
              onEditCustomerNotes={canAccess.editBookingDetails ? handleSaveCustomerNotes : undefined}
              bookingSource={booking.source}
              onSourceUpdate={canAccess.editBookingDetails ? handleSourceUpdate : undefined}
              businessId={selectedBusiness?.id}
              bookingId={booking.id}
            />
          </div>
        </div>



        {/* Services Section */}
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden shadow-sm">
          <BookingServiceList
            services={booking.services || []}
            onAddService={canAccess.editBookingDetails ? onAddService : undefined}
            onEditVehicle={canAccess.editBookingDetails ? onEditVehicle : undefined}
            onUpdate={onUpdate}
            onError={onError}
            customer={booking.customer}
            showSalesInfo={canAccess.bookingSalesInfo}
            canEdit={canAccess.editBookingDetails}
            bookingWorkflows={booking.booking_workflows || []}
            bookingStatus={booking.status}
            bookingId={booking.id}
            onAssignWorkflow={onAssignWorkflow}
          />
        </div>

        {/* Products Section */}
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden shadow-sm">
          <BookingProductList
            bookingId={booking.id}
            onUpdate={onUpdate}
            onError={onError}
            showSalesInfo={canAccess.bookingSalesInfo}
            canEdit={canAccess.editBookingDetails}
          />
        </div>

        {/* Resources Section */}
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden shadow-sm">
          <BookingResourceList
            resources={booking.resources || []}
            onRemoveResource={canAccess.editBookingDetails ? onRemoveResource : undefined}
            onAddResource={canAccess.editBookingDetails ? onAddResource : undefined}
          />
        </div>

        {/* Commission Section - Only shows if there are commission-based resources */}
        <BookingCommissionSection
          booking={booking}
          onUpdate={onUpdate}
          onError={onError}
        />

        {canAccess.bookingSalesInfo && (
          <div className="bg-white rounded-lg border border-gray-200 overflow-hidden shadow-sm">
            <BookingPaymentSummary
              subtotal={booking.subtotal || 0}
              discounts={booking.discounts as any}
              taxAmount={booking.tax_amount || 0}
              totalAmount={booking.total_amount || 0}
              payments={booking.payments as any}
              onApplyDiscount={onApplyDiscount}
              onDeleteDiscount={onDeleteDiscount}
              onRecordPayment={handleRecordPayment}
              onViewInvoiceBeta={handleViewInvoiceBeta}
            />
          </div>
        )}

        <ApplyDiscountModal
          isOpen={isApplyDiscountModalOpen}
          onClose={() => setIsApplyDiscountModalOpen(false)}
          bookingId={booking.id}
          bookingTotal={booking.total_amount || 0}
          onApply={onUpdate}
        />

        <RecordPaymentModal
          isOpen={isRecordPaymentModalOpen}
          onClose={() => setIsRecordPaymentModalOpen(false)}
          onSuccess={onUpdate}
          bookingId={booking.id}
          businessId={booking.business_id || ''}
          bookingTotal={booking.total_amount || 0}
          amountPaid={booking.payments?.reduce((sum, payment) => 
            payment.status === 'completed' ? sum + payment.amount : sum, 0) || 0}
        />

        <PaymentDetailsModal
          isOpen={isPaymentDetailsModalOpen}
          onClose={() => {
            setIsPaymentDetailsModalOpen(false);
            setSelectedPayment(null);
          }}
          bookingId={booking.id}
          allPayments={allPaymentDetails}
          onUpdate={onUpdate}
        />


        
        <BetaPaymentsModal
          isOpen={isBetaPaymentsModalOpen}
          onClose={() => setIsBetaPaymentsModalOpen(false)}
          bookingId={booking.id}
          bookingNumber={typeof booking.booking_number === 'number' ? booking.booking_number : parseInt(String(booking.booking_number)) || 0}
          bookingData={{
            total_amount: booking.total_amount || 0,
            customer: booking.customer ? {
              id: booking.customer.id,
              first_name: booking.customer.first_name || '',
              last_name: booking.customer.last_name || '',
              email: booking.customer.email || undefined,
              phone: booking.customer.phone || undefined,
              full_name: `${booking.customer.first_name || ''} ${booking.customer.last_name || ''}`.trim() || undefined
            } : undefined,
            payments: booking.payments?.map(p => ({
              id: p.id,
              amount: p.amount,
              tip_amount: (p as any).tip_amount || undefined,
              base_amount: (p as any).base_amount || undefined,
              status: p.status,
              pdf_url: (p as any).pdf_url,
              created_at: p.created_at,
              reference: (p as any).reference || undefined,
              stripe_payment_intent_id: (p as any).stripe_payment_intent_id || undefined,
              stripe_checkout_session_id: (p as any).stripe_checkout_session_id || undefined,
              notes: (p as any).notes || undefined
            })) || []
          }}
          booking={booking}
          onUpdate={onUpdate}
          isInvisible={isInvisiblePDFGeneration}
        />

        <SendInvoiceModal
          isOpen={isSendInvoiceModalOpen}
          onClose={() => setIsSendInvoiceModalOpen(false)}
          onSelectMethod={handleSendMethodSelect}
          customerEmail={booking.customer?.email || undefined}
          customerPhone={booking.customer?.phone || undefined}
          hasStripeConnected={!!(selectedBusiness as any)?.stripe_connect_account_id}
        />

        <SendLiabilityWaiverModal
          isOpen={isSendLiabilityWaiverModalOpen}
          onClose={() => setIsSendLiabilityWaiverModalOpen(false)}
          onSendSMS={handleSendLiabilityWaiverSMS}
          onSendEmail={handleSendLiabilityWaiverEmail}
          onSignOnDevice={handleSignLiabilityWaiverOnDevice}
          customerName={`${booking.customer?.first_name || ''} ${booking.customer?.last_name || ''}`.trim()}
          customerPhone={booking.customer?.phone}
          customerEmail={booking.customer?.email || undefined}
          isLoading={isSendingLiabilityWaiver}
        />

        <VehicleDamageReportModal
          isOpen={isDamageReportModalOpen}
          onClose={() => setIsDamageReportModalOpen(false)}
          bookingId={booking.id}
          existingReport={damageReport}
          vehicleInfo={{
            make: booking.services?.[0]?.vehicle_make || undefined,
            model: booking.services?.[0]?.vehicle_model || undefined,
            year: undefined,
            color: undefined,
            licensePlate: undefined
          }}
          onSave={handleDamageReportSave}
        />

        {/* Portal Toast for Modal Context */}
        {showPortalToast && (
          <PortalToast
            type={portalToastType}
            onClose={() => setShowPortalToast(false)}
          >
            {portalToastMessage}
          </PortalToast>
        )}
      </div>
    );
  }

  if (activeTab === 'materials') {
    return (
      <BookingMaterialsTab
        materials={booking.materials || []}
        onAddMaterial={async (material, quantity) => {
          try {
            const { error: addError } = await supabase!
              .from('booking_materials')
              .insert({
                booking_id: booking.id,
                material_id: material.id,
                material_name: material.name,
                material_description: material.description,
                quantity,
                unit: material.unit,
                cost: material.cost,
                total_cost: material.cost * quantity
              });

            if (addError) throw addError;

            await onUpdate();
          } catch (err) {
            console.error('Error adding material:', err);
            onError(err instanceof Error ? err.message : 'Failed to add material');
          }
        }}
        onRemoveMaterial={async (id) => {
          try {
            const { error: deleteError } = await supabase!
              .from('booking_materials')
              .delete()
              .eq('id', id);

            if (deleteError) throw deleteError;

            await onUpdate();
          } catch (err) {
            console.error('Error removing material:', err);
            onError(err instanceof Error ? err.message : 'Failed to remove material');
          }
        }}
        onUpdate={onUpdate}
        onError={onError}
        onOpenMaterialModal={() => setIsSelectMaterialModalOpen(true)}
        booking={booking}
      />
    );
  }

  if (activeTab === 'flat-rates') {
    return (
      <BookingFlatRatesTab
        bookingId={booking.id}
        businessId={booking.business_id}
        onUpdate={onUpdate}
        onError={onError}
        booking={booking}
      />
    );
  }

  if (activeTab === 'profit-loss') {
    return (
      <BookingProfitLoss
        revenue={booking.subtotal || 0}
        materialCosts={booking.materials?.reduce((sum, material) => 
          sum + (material.total_cost || 0), 0) || 0}
        discounts={booking.discounts?.reduce((sum, discount) => {
          if (discount.type === 'percentage') {
            return sum + (booking.subtotal * discount.amount / 100);
          }
          return sum + discount.amount;
        }, 0) || 0}
        laborCosts={booking.flat_rates?.reduce((sum, rate) => {
          let rateAmount = 0;
          if ((rate as any).resource_flat_rate_amount !== null && (rate as any).resource_flat_rate_amount !== undefined) {
            rateAmount = Number((rate as any).resource_flat_rate_amount);
          } else if ((rate as any).resource_hourly_rate !== null && (rate as any).resource_hourly_rate !== undefined) {
            rateAmount = Number((rate as any).resource_hourly_rate);
          }
          
          return sum + (rate.hours * rateAmount);
        }, 0) || 0}
        bookingId={booking.id}
        flatRates={booking.flat_rates || []}
      />
    );
  }

  return null;
}